import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useCallback,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { ChatMessage, RuntimeEvent, ToolActivity } from '../types';

// ── State ─────────────────────────────────────────────────────────────────────

export interface NoraState {
  messages: ChatMessage[];
  streaming: boolean;
  error: string | null;
}

const INITIAL: NoraState = {
  messages: [],
  streaming: false,
  error: null,
};

// ── Actions ───────────────────────────────────────────────────────────────────

type Action =
  | { type: 'USER_MESSAGE'; id: string; text: string; images?: string[] }
  | { type: 'ASSISTANT_START'; id: string }
  | { type: 'TEXT_DELTA'; delta: string }
  | { type: 'TOOL_START'; activity: ToolActivity }
  | { type: 'TOOL_PERMISSION'; activity: ToolActivity }
  | { type: 'TOOL_PERMISSION_DECIDE'; toolUseId: string; approved: boolean }
  | { type: 'TOOL_PERMISSION_FAILED'; toolUseId: string }
  | {
      type: 'TOOL_DONE';
      toolUseId: string;
      isError: boolean;
      kubernetes?: import('../types').KubernetesToolData;
      webSearch?: import('../types').WebSearchToolData;
    }
  | { type: 'TURN_DONE' }
  | { type: 'MESSAGE_PERSISTED'; role: 'user' | 'assistant'; id: string }
  | { type: 'ERROR'; message: string }
  | { type: 'NEW_CONVERSATION' }
  | { type: 'LOAD_CONVERSATION'; messages: ChatMessage[] };

// ── Reducer ───────────────────────────────────────────────────────────────────

// A confirm card left 'pending' (never answered) when the turn ends for any
// reason (stream error, abort, navigation) can never be approved after the
// fact — settle it locally so the UI never shows a stuck, unusable card.
// 'executing' entries are left alone: the backend has already been told
// approved=true and may have applied the mutation even if the SSE stream
// itself then broke.
function settlePendingActivity(messages: ChatMessage[]): ChatMessage[] {
  return messages.map(msg => {
    if (msg.role !== 'assistant' || !msg.activity) return msg;
    if (!msg.activity.some(entry => entry.status === 'pending')) return msg;
    return {
      ...msg,
      activity: msg.activity.map(entry =>
        entry.status === 'pending' ? { ...entry, status: 'denied' } : entry,
      ),
    };
  });
}

function reducer(state: NoraState, action: Action): NoraState {
  switch (action.type) {
    case 'USER_MESSAGE': {
      const msg: ChatMessage = {
        id: action.id,
        role: 'user',
        text: action.text,
        timestamp: Date.now(),
        images: action.images?.length ? action.images : undefined,
      };
      return {
        ...state,
        messages: [...state.messages, msg],
        streaming: true,
        error: null,
      };
    }

    case 'ASSISTANT_START':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: action.id,
            role: 'assistant',
            text: '',
            timestamp: Date.now(),
          },
        ],
      };

    case 'TEXT_DELTA': {
      const next = [...state.messages];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant') {
          next[i] = { ...next[i], text: next[i].text + action.delta };
          break;
        }
      }
      return { ...state, messages: next };
    }

    case 'TOOL_START': {
      const next = [...state.messages];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant') {
          const activity = next[i].activity ?? [];
          // A tool.permission confirm card for this same toolUseId may
          // already be showing (pending/executing/denied) — update it in
          // place instead of appending a duplicate row.
          const idx = activity.findIndex(
            entry => entry.id === action.activity.id,
          );
          next[i] = {
            ...next[i],
            activity:
              idx >= 0
                ? activity.map((entry, j) =>
                    j === idx &&
                    entry.status !== 'executing' &&
                    entry.status !== 'denied'
                      ? { ...entry, status: 'running' }
                      : entry,
                  )
                : [...activity, action.activity],
          };
          break;
        }
      }
      return { ...state, messages: next };
    }

    case 'TOOL_PERMISSION': {
      const next = [...state.messages];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant') {
          next[i] = {
            ...next[i],
            activity: [...(next[i].activity ?? []), action.activity],
          };
          break;
        }
      }
      return { ...state, messages: next };
    }

    case 'TOOL_PERMISSION_DECIDE': {
      const next = state.messages.map(msg => {
        if (msg.role !== 'assistant' || !msg.activity) return msg;
        if (!msg.activity.some(entry => entry.id === action.toolUseId)) {
          return msg;
        }
        return {
          ...msg,
          activity: msg.activity.map(entry =>
            entry.id === action.toolUseId
              ? {
                  ...entry,
                  status: action.approved
                    ? ('executing' as const)
                    : ('denied' as const),
                }
              : entry,
          ),
        };
      });
      return { ...state, messages: next };
    }

    case 'TOOL_PERMISSION_FAILED': {
      const next = state.messages.map(msg => {
        if (msg.role !== 'assistant' || !msg.activity) return msg;
        return {
          ...msg,
          activity: msg.activity.map(entry =>
            entry.id === action.toolUseId
              ? { ...entry, status: 'error' as const }
              : entry,
          ),
        };
      });
      return { ...state, messages: next };
    }

    case 'TOOL_DONE': {
      const next = [...state.messages];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant') {
          next[i] = {
            ...next[i],
            activity: (next[i].activity ?? []).map(entry =>
              entry.id === action.toolUseId
                ? {
                    ...entry,
                    status: action.isError ? 'error' : 'done',
                    kubernetes: action.kubernetes,
                    webSearch: action.webSearch,
                  }
                : entry,
            ),
          };
          break;
        }
      }
      return { ...state, messages: next };
    }

    case 'TURN_DONE':
      return {
        ...state,
        streaming: false,
        messages: settlePendingActivity(state.messages),
      };

    case 'MESSAGE_PERSISTED': {
      const next = [...state.messages];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === action.role && !next[i].backendId) {
          next[i] = { ...next[i], backendId: action.id };
          break;
        }
      }
      return { ...state, messages: next };
    }

    case 'ERROR':
      return {
        ...state,
        streaming: false,
        error: action.message,
        messages: settlePendingActivity(state.messages),
      };

    case 'NEW_CONVERSATION':
      return INITIAL;

    case 'LOAD_CONVERSATION':
      return { ...INITIAL, messages: action.messages };

    default:
      return state;
  }
}

// ── Context ───────────────────────────────────────────────────────────────────

interface NoraContext {
  state: NoraState;
  dispatch: React.Dispatch<Action>;
}

const Ctx = createContext<NoraContext | null>(null);

export function NoraProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  return <Ctx.Provider value={{ state, dispatch }}>{children}</Ctx.Provider>;
}

export function useNora() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useNora must be inside <NoraProvider>');
  return ctx;
}

// ── Dispatch helper: consume a RuntimeEvent stream → store ────────────────────

function toolLabel(toolName: string, args: unknown): string {
  const input = (args ?? {}) as Record<string, unknown>;
  switch (toolName) {
    case 'k8s_list_pods':
      return `Listing pods in ${input.namespace ?? '?'} (${
        input.cluster ?? '?'
      })`;
    case 'k8s_diagnose_pod':
      return `Diagnosing pod ${input.pod ?? '?'} in ${input.namespace ?? '?'}`;
    case 'k8s_pod_logs':
      return `Reading logs of ${input.pod ?? '?'}`;
    case 'k8s_get':
      return `Querying ${input.cluster ?? 'cluster'}: ${input.path ?? ''}`;
    case 'web_search':
      return `Searching the web: ${input.query ?? ''}`;
    case 'k8s_restart_rollout':
      return `Restart rollout of ${input.kind ?? 'workload'} ${
        input.name ?? '?'
      } in ${input.namespace ?? '?'}`;
    case 'k8s_scale':
      return `Scale ${input.kind ?? 'workload'} ${input.name ?? '?'} to ${
        input.replicas ?? '?'
      } replicas`;
    default:
      return `Running ${toolName}`;
  }
}

const KNOWN_TOOLS = [
  'k8s_list_pods',
  'k8s_diagnose_pod',
  'k8s_pod_logs',
  'k8s_get',
  'web_search',
  'k8s_restart_rollout',
  'k8s_scale',
];

function safeToolInput(
  toolName: string,
  args: unknown,
): Record<string, string | number | boolean> | undefined {
  if (
    !KNOWN_TOOLS.includes(toolName) ||
    !args ||
    typeof args !== 'object' ||
    Array.isArray(args)
  ) {
    return undefined;
  }
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(args)) {
    if (
      [
        'cluster',
        'namespace',
        'pod',
        'container',
        'path',
        'query',
        'filter',
        'previous',
        'tailLines',
        'kind',
        'name',
        'replicas',
      ].includes(key) &&
      (typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean')
    ) {
      safe[key] = value;
    }
  }
  return Object.keys(safe).length ? safe : undefined;
}

export function useRuntimeEventDispatch() {
  const { dispatch } = useNora();
  const assistantId = useRef<string>('');

  const ensureAssistant = useCallback(() => {
    if (!assistantId.current) {
      assistantId.current = uuidv4();
      dispatch({ type: 'ASSISTANT_START', id: assistantId.current });
    }
  }, [dispatch]);

  return useCallback(
    (event: RuntimeEvent) => {
      switch (event.type) {
        case 'text.delta':
          ensureAssistant();
          dispatch({
            type: 'TEXT_DELTA',
            delta: (event as { delta: string }).delta,
          });
          break;

        case 'tool.start': {
          ensureAssistant();
          const start = event as {
            toolUseId: string;
            toolName: string;
            args: unknown;
          };
          dispatch({
            type: 'TOOL_START',
            activity: {
              id: start.toolUseId,
              label: toolLabel(start.toolName, start.args),
              status: 'running',
              toolName: start.toolName,
              input: safeToolInput(start.toolName, start.args),
            },
          });
          break;
        }

        case 'tool.permission': {
          ensureAssistant();
          const perm = event as {
            toolUseId: string;
            toolName: string;
            input: unknown;
            message: string;
            isDestructive: boolean;
            preview?: import('../types').KubernetesMutationPreview | null;
          };
          dispatch({
            type: 'TOOL_PERMISSION',
            activity: {
              id: perm.toolUseId,
              label: toolLabel(perm.toolName, perm.input),
              status: 'pending',
              toolName: perm.toolName,
              input: safeToolInput(perm.toolName, perm.input),
              confirm: {
                message: perm.message,
                isDestructive: perm.isDestructive,
                preview: perm.preview ?? undefined,
              },
            },
          });
          break;
        }

        case 'tool.result': {
          const result = event as {
            toolUseId: string;
            isError: boolean;
            meta?: {
              kind?: string;
              data?:
                | import('../types').KubernetesToolData
                | import('../types').WebSearchToolData;
            };
          };
          dispatch({
            type: 'TOOL_DONE',
            toolUseId: result.toolUseId,
            isError: Boolean(result.isError),
            kubernetes:
              result.meta?.kind === 'kubernetes'
                ? (result.meta.data as import('../types').KubernetesToolData)
                : undefined,
            webSearch:
              result.meta?.kind === 'webSearch'
                ? (result.meta.data as import('../types').WebSearchToolData)
                : undefined,
          });
          break;
        }

        case 'turn.done':
          assistantId.current = '';
          dispatch({ type: 'TURN_DONE' });
          break;

        case 'message.persisted': {
          const persisted = event as { role: 'user' | 'assistant'; id: string };
          dispatch({
            type: 'MESSAGE_PERSISTED',
            role: persisted.role,
            id: persisted.id,
          });
          break;
        }

        case 'error':
          assistantId.current = '';
          dispatch({
            type: 'ERROR',
            message: (event as { message: string }).message,
          });
          break;

        default:
          // file.changed / tool.userQuestion events are not part of Nora's
          // chat UI — ignore. tool.permission is handled explicitly above.
          break;
      }
    },
    [dispatch, ensureAssistant],
  );
}

export type { Action };
